import { roundDecimal } from './product-bundle-utils';

// Σ of each item's (original × quantity), rounded per item and per accumulation.
const sumBundleSummaryPrice = (products) => (products || []).reduce((sum, product) => {
  const quantity = product?.quantity || 1;
  const original = product?.pricing?.original || 0;
  return roundDecimal(sum + roundDecimal(original * quantity));
}, 0);

/**
 * Sums bundle-level savings from bundlePromotionalAdjustments.
 * Footgun: parentBundleAdjustments (from the parent `product` call) is authoritative on pristine load;
 * once the bundle is modified it goes stale, so callers pass null and per-item adjustments are used.
 * Item-level (untagged) discounts are NOT aggregated here — they're already reflected in each item's
 * price via getItemPrices/getNormalizedItemValue; only bundle-level (tagged) promos are summed.
 * When suppressBundlePromos is true, bundle-level promos are kept out of the advertised total (parked in
 * suppressedBundleSavings so the cart price still reflects them) — see addBundleLevelPromo.
 * @returns {Object} { bundleSavings, suppressedBundleSavings, bundleSavingsByPromos, bundleSummaryPrice }
 */
export const extractBundleAdjustments = (products, parentBundleAdjustments, suppressBundlePromos = false) => {
  let bundleSavings = 0;
  let suppressedBundleSavings = 0;
  const bundleSavingsByPromos = {};

  // When suppressed, the promo keeps its line item (descriptions, rendered as a dash) but goes to
  // suppressedBundleSavings instead of bundleSavings, so it's excluded from the advertised total.
  const addBundleLevelPromo = (adj, dollarOff) => {
    const promoId = adj.promoId || 'unknown';

    if (bundleSavingsByPromos[promoId]) {
      if (!suppressBundlePromos) {
        bundleSavingsByPromos[promoId].savings = roundDecimal(
          bundleSavingsByPromos[promoId].savings + dollarOff
        );
      }
    } else {
      bundleSavingsByPromos[promoId] = {
        promoId,
        savings: suppressBundlePromos ? 0 : dollarOff,
        shortDesc: adj.description?.shortDesc || '',
        longDesc: adj.description?.longDesc || '',
        startDt: adj.dates?.start || null,
        endDt: adj.dates?.end || null,
        ...(suppressBundlePromos ? { suppressed: true } : {})
      };
    }

    if (suppressBundlePromos) {
      suppressedBundleSavings = roundDecimal(suppressedBundleSavings + dollarOff);
    } else {
      bundleSavings = roundDecimal(bundleSavings + dollarOff);
    }
  };

  const bundleSummaryPrice = sumBundleSummaryPrice(products);

  // On page load (pristine state), parentBundleAdjustments is the authoritative source from the
  // `product` call. Use it directly instead of per-item adjustments from the `products` call.
  if (parentBundleAdjustments?.length) {
    parentBundleAdjustments.forEach((adj) => {
      if (adj.promotionTag) {
        addBundleLevelPromo(adj, roundDecimal(adj.dollarOff || 0));
      }
    });

    return {
      bundleSavings,
      suppressedBundleSavings,
      bundleSavingsByPromos,
      bundleSummaryPrice
    };
  }

  (products || []).forEach((product) => {
    const adjustments = product?.pricing?.bundlePromotionalAdjustments || [];

    adjustments.forEach((adj) => {
      if (adj.promotionTag) {
        // Bundle-level promos (BMSM/Exchange) are already the whole-bundle total — use as-is, never × quantity.
        addBundleLevelPromo(adj, roundDecimal(adj.dollarOff || 0));
      }
    });
  });

  return {
    bundleSavings,
    suppressedBundleSavings,
    bundleSavingsByPromos,
    bundleSummaryPrice
  };
};
